<template>
  <div class="d-flex flex-row actions-row align-center">
    <v-spacer></v-spacer>

    <v-tooltip :max-width="'184px'" :disabled="disableTooltip1" top>
      <template v-slot:activator="{ on, attrs }">
        <div class="action-button" v-bind="attrs" v-on="on">
          <base-btn
            v-if="showAction1"
            :disabled="disabledAction1"
            :loading="loadingAction1"
            type="tertiary"
            @click="handleAction1"
          >
            {{ finalAction1Label }}
          </base-btn>
        </div>
      </template>
      <span>{{ finalAction1TooltipText }}</span>
    </v-tooltip>

    <v-tooltip :max-width="'184px'" :disabled="disableTooltip2" top>
      <template v-slot:activator="{ on, attrs }">
        <div class="action-button pl-5" v-bind="attrs" v-on="on">
          <base-btn
            v-if="showAction2"
            :disabled="disabledAction2"
            :loading="loadingAction2"
            @click="handleAction2"
          >
            {{ finalAction2Label }}
          </base-btn>
        </div>
      </template>
      <span>{{ finalAction2TooltipText }}</span>
    </v-tooltip>

    <div
      v-if="isMobileBreakpoint"
      class="ml-5 d-flex align-center justify-center cursor-pointer"
      style="width: 40px; height: 40px; flex-shrink: 0; border-radius: 48px; position: relative; overflow: hidden; background-color: #006AEB;"
      @click="toggleCrispChat"
    >
      <img
        :src="require('@/assets/crisp.svg')"
        alt="Crisp Chat"
        :class="[ 'icon', { 'visible-chat-icon': !showCrispChat, 'hidden-icon': showCrispChat } ]"
      />
      <img
        :src="require('@/assets/close.svg')"
        alt="Close Chat"
        :class="[ 'icon', showCrispChat ? 'visible-close-icon' : 'hidden-close-icon' ]"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'ActionButtonsWithCrisp',
  props: {
    showAction1: {
      type: Boolean,
      default: true
    },
    showAction2: {
      type: Boolean,
      default: true
    },
    disableTooltip1: {
      type: Boolean,
      default: false
    },
    disableTooltip2: {
      type: Boolean,
      default: false
    },
    disabledAction1: {
      type: Boolean,
      default: false
    },
    disabledAction2: {
      type: Boolean,
      default: false
    },
    handleAction1: {
      type: Function,
      required: true
    },
    handleAction2: {
      type: Function,
      required: true
    },
    loadingAction1: {
      type: Boolean,
      default: false
    },
    loadingAction2: {
      type: Boolean,
      default: false
    },
    action1Label: {
      type: String,
      default: ''
    },
    action2Label: {
      type: String,
      default: ''
    },
    action1TooltipText: {
      type: String,
      default: ''
    },
    action2TooltipText: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      showCrispChat: false,
      isTransitioning: false
    }
  },
  mounted() {
    if (this.isMobileBreakpoint) {
      this.hideCrispFloatingButton()
    } else {
      this.showCrispFloatingButton()
    }

    this.$crisp.push(['on', 'chat:closed', () => {
      this.showCrispChat = false
    }])

    this.$crisp.push(['on', 'chat:opened', () => {
      if (this.showCrispChat && this.isMobileBreakpoint) {
        this.hideCrispFloatingButton()
      }
    }])

    this.showCrispChat = this.$crisp.is('chat:opened')
  },
  beforeDestroy() {
    this.showCrispFloatingButton()
    this.$crisp.push(['off', 'chat:closed'])
    this.$crisp.push(['off', 'chat:opened'])
  },
  computed: {
    ...mapState({
      isMobileBreakpoint: (state) => state.user.isMobileBreakpoint,
    }),
    finalAction1Label() {
      return this.action1Label || this.$t('message.actions.cancel')
    },
    finalAction2Label() {
      return this.action2Label || this.$t('message.actions.publish')
    },
    finalAction1TooltipText() {
      return this.action1TooltipText || this.$t('message.noChanges')
    },
    finalAction2TooltipText() {
      return this.action2TooltipText || this.$t('message.noChanges')
    }
  },
  watch: {
    isMobileBreakpoint(newVal) {
      if (newVal) {
        this.hideCrispFloatingButton()
      } else {
        this.showCrispFloatingButton()
      }
    }
  },
  methods: {
    toggleCrispChat() {
      if (this.isTransitioning) return

      this.isTransitioning = true

      if (!this.showCrispChat) {
        this.$crisp.push(['do', 'chat:show'])
        this.$crisp.push(['do', 'chat:open'])
      } else {
        this.$crisp.push(['do', 'chat:close'])
      }
      this.showCrispChat = !this.showCrispChat

      setTimeout(() => {
        this.isTransitioning = false
      }, 300)
    },
    hideCrispFloatingButton() {
      const crispButton = document.querySelector('.crisp-client')
      const crispIcon = crispButton?.querySelector('.cc-1m2mf')
      if (crispIcon) {
        crispIcon.style.setProperty('display', 'none', 'important')
      }
    },
    showCrispFloatingButton() {
      const crispButton = document.querySelector('.crisp-client')
      const crispIcon = crispButton?.querySelector('.cc-1m2mf')
      if (crispIcon) {
        crispIcon.style.removeProperty('display')
      }
    }
  },
}
</script>

<style scoped>
.icon {
  width: 40px;
  height: 40px;
  object-fit: contain;
  position: absolute;
  transition: opacity 0.25s ease, transform 0.3s ease;
}

.visible-chat-icon {
  opacity: 1;
  z-index: 2;
  transform: rotate(0deg) scale(1);
  transition-delay: 0.30s;
}

.hidden-icon {
  opacity: 0;
  z-index: 1;
  transform: rotate(0deg) scale(0.9);
  pointer-events: none;
  transition-delay: 0s;
}

.visible-close-icon {
  opacity: 1;
  z-index: 2;
  transform: rotate(0deg) scale(1);
  transition-delay: 0s;
}

.hidden-close-icon {
  opacity: 0;
  z-index: 1;
  transform: rotate(-15deg) scale(0.9);
  pointer-events: none;
  transition-delay: 0s;
}
</style>
